import { Component } from '@theme/component';
import { debounce, expandPartNumberQuery } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleFormSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleFormSubmit);
  }

  /**
   * Prefix part numbers with # so search-page submit matches Shopify SKU/part search.
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    const input = this.refs.searchPageInput;
    const expanded = expandPartNumberQuery(input.value);
    if (expanded === input.value) return;

    event.preventDefault();
    const searchUrl = new URL(input.form?.action || Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', expanded);
    searchUrl.searchParams.set('type', 'product');
    window.location.href = searchUrl.toString();
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
